import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Loader2, Paperclip, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useUploadArtifacts } from '../../../hooks/useDeliveryDocuments';
const ACCEPT = 'application/pdf';
/**
 * File-upload section for a scored record's source document (CPARS / QASP).
 * Uploads a bare tenant artifact via tesoro and hands the id back to the
 * form, which stores it on the score — the score row is the system of record,
 * so the upload has no dependency on the program's backing contract project.
 * This only attaches the source; extracting field values from it is a
 * separate step in this same drawer (IntelligenceReextractButton, below).
 */ export function ScoreDocumentUpload({ label, onUploaded, attachedName, onUploadingChange }) {
    const upload = useUploadArtifacts();
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [fileName, setFileName] = useState('');
    const setUploading = (next)=>{
        setBusy(next);
        onUploadingChange?.(next);
    };
    const onPick = async (e)=>{
        const files = Array.from(e.target.files ?? []);
        e.target.value = '';
        if (files.length === 0) return;
        // Eager upload: the artifact is uploaded now, before the score is saved.
        // If the user then cancels the form (or the upsert fails), the artifact
        // stays in the tenant's store linked to no score — a harmless orphan. A
        // cancel-time cleanup is a deliberate follow-up, not done here.
        setUploading(true);
        try {
            const result = await upload.mutateAsync({
                files
            });
            result.failed?.forEach((f)=>toast.error(`Failed to upload ${f.name}: ${f.error}`));
            const id = result.files.map((f)=>f.artifactId).filter(Boolean)[0];
            if (id) {
                setFileName(files[0].name);
                onUploaded(id, files[0].name);
                toast.success('PDF uploaded — save the score to attach it.');
            } else if (!result.failed?.length) {
                // No id and no per-file failure — don't leave the picker silently doing nothing.
                toast.error("Couldn't attach that file. Please try again.");
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Upload failed');
        } finally{
            setUploading(false);
        }
    };
    // Detach: send an empty id back so the full-replace upsert stores NULL,
    // letting a user who attached the wrong PDF get back to "no document".
    const handleRemove = ()=>{
        setFileName('');
        onUploaded('', '');
    };
    const shown = fileName || attachedName;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        "data-testid": Testids.deliveryQuality.scoreDocumentUpload,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "text-sm font-medium",
                children: label
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Attach the source PDF, then use Extract below to process it right here in this drawer."
            }),
            /*#__PURE__*/ _jsx("input", {
                ref: inputRef,
                type: "file",
                accept: ACCEPT,
                className: "hidden",
                disabled: busy,
                onChange: onPick
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        size: "sm",
                        variant: "secondary",
                        disabled: busy,
                        onClick: ()=>inputRef.current?.click(),
                        children: busy ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Loader2, {
                                    className: "mr-1.5 h-3.5 w-3.5 animate-spin"
                                }),
                                "Uploading…"
                            ]
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Upload, {
                                    className: "mr-1.5 h-3.5 w-3.5"
                                }),
                                "Choose PDF"
                            ]
                        })
                    }),
                    shown && /*#__PURE__*/ _jsxs("span", {
                        className: "flex items-center gap-1 text-xs text-muted-foreground",
                        children: [
                            /*#__PURE__*/ _jsx(Paperclip, {
                                className: "h-3 w-3"
                            }),
                            shown,
                            /*#__PURE__*/ _jsxs(SpecraButton, {
                                type: "button",
                                size: "sm",
                                variant: "destructive",
                                className: "h-5 gap-1 px-1",
                                disabled: busy,
                                onClick: handleRemove,
                                children: [
                                    /*#__PURE__*/ _jsx(X, {
                                        className: "h-3 w-3"
                                    }),
                                    "Remove"
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
